import { phpBrands } from "../../../core/php-data-access";
import { translator } from "../../../translation/translator";
import { SearchBrandModalSetup } from "../../../search/brand/modal/search-brand-modal-setup";
import { ChipPickerHandler } from "../chips/chip-picker-handler";
import { ChipPickerListener } from "../chips/chip-picker-listener";
import { SearchBrandPickerForm } from "./search-brand-picker-form";
import type { PhpBrandData } from "../../../types/php-injected";

type UnknownRecord = Record<string, unknown>;

type SearchBrandPickerSourceItem = unknown | PhpBrandData;

interface SearchBrandPickerItem {
    id: string;
    name: string;
}

interface SearchBrandModalSourceItem {
    brand_id: string;
    brand_name: string;
    brand_index?: string;
    products_count?: number | null;
}

interface SearchBrandPickerApiData {
    popular_brands?: SearchBrandPickerSourceItem[];
    all_brands?: SearchBrandPickerSourceItem[];
}

function toBrandItem(raw: SearchBrandPickerSourceItem): SearchBrandPickerItem | null {
    const obj = typeof raw === "object" && raw !== null && !Array.isArray(raw) ? (raw as UnknownRecord) : null;
    if (!obj) return null;
    const id = String(("item_id" in obj ? obj.item_id : undefined) ?? obj.brand_id ?? "").trim();
    const name = String(("item_name" in obj ? obj.item_name : undefined) ?? obj.brand_name ?? "").trim();
    if (!id || !name) return null;
    return { id, name };
}

function isSearchBrandPickerItem(item: SearchBrandPickerItem | null): item is SearchBrandPickerItem {
    return item !== null;
}

function toSearchBrandModalSourceItem(raw: SearchBrandPickerSourceItem): SearchBrandModalSourceItem | null {
    const brand = toBrandItem(raw);
    if (!brand) return null;

    const obj = typeof raw === "object" && raw !== null && !Array.isArray(raw) ? (raw as UnknownRecord) : null;
    const countRaw = obj?.["products_count"] ?? obj?.["productsCount"];
    const productsCount = typeof countRaw === "number" ? countRaw : countRaw != null ? Number(countRaw) : null;

    return {
        brand_id: brand.id,
        brand_name: brand.name,
        brand_index: typeof obj?.["brand_index"] === "string" ? String(obj["brand_index"]).trim() : undefined,
        products_count: Number.isFinite(productsCount) ? Math.max(0, Math.trunc(productsCount)) : null,
    };
}

function createBrandNameMap(): Map<string, string> {
    return new Map();
}

export interface SearchBrandPickerSetupParams {
    menuRoot: HTMLElement;
    apiData: unknown;
    initialSelectedIds: string[];
    onSelectionChange: (ids: string[]) => void;
}

export class SearchBrandPickerSetup {
    public static setupForSearchMenu(params: SearchBrandPickerSetupParams): ChipPickerHandler | null {
        const { menuRoot, apiData, initialSelectedIds, onSelectionChange } = params;

        const pickerRoot = SearchBrandPickerForm.getPickerRoot(menuRoot);
        const chipsEl = SearchBrandPickerForm.getChips(menuRoot);

        if (!(pickerRoot instanceof HTMLElement) || !(chipsEl instanceof HTMLElement)) {
            return null;
        }

        const modal = SearchBrandModalSetup.init(document);
        if (!modal) return null;

        const obj =
            typeof apiData === "object" && apiData !== null && !Array.isArray(apiData)
                ? (apiData as SearchBrandPickerApiData)
                : null;
        const popularBrandsRaw = obj && Array.isArray(obj.popular_brands) ? obj.popular_brands : [];
        const allBrandsFromApi = obj && Array.isArray(obj.all_brands) ? obj.all_brands : null;
        const allBrandsRaw: SearchBrandPickerSourceItem[] = allBrandsFromApi ?? phpBrands();
        const popularBrands = popularBrandsRaw
            .map(toSearchBrandModalSourceItem)
            .filter((brand): brand is SearchBrandModalSourceItem => brand !== null);
        const allBrands = (Array.isArray(allBrandsRaw) ? allBrandsRaw : [])
            .map(toSearchBrandModalSourceItem)
            .filter((brand): brand is SearchBrandModalSourceItem => brand !== null);

        const nameById = createBrandNameMap();
        popularBrandsRaw
            .map(toBrandItem)
            .filter(isSearchBrandPickerItem)
            .forEach((brand) => nameById.set(String(brand.id), String(brand.name)));
        allBrandsRaw
            .map(toBrandItem)
            .filter(isSearchBrandPickerItem)
            .forEach((brand) => nameById.set(String(brand.id), String(brand.name)));

        modal.setData({ popularBrands, allBrands });
        modal.setSelectedIds(initialSelectedIds);

        const controller = new ChipPickerHandler({
            chipsEl,
            openBtnEl: pickerRoot,
            idAttribute: "data-brand-id",
            removeAction: "remove",
            renderOptions: {
                chipClass: "picker-chip",
                labelClass: "picker-chip__label",
                removeClass: "picker-chip__remove",
                removeAriaLabel: "Remove",
                emptyHtml:
                    '<span class="input-placeholder search-item" data-translate="select_brand">Select brand</span>',
            },
        });

        controller.setLabelMap(nameById);
        controller.setSelectedIds(initialSelectedIds);
        controller.setOnSelectionChange(onSelectionChange);
        controller.setExternalSelectionUI({
            setSelectedIds: (ids: string[]) => modal.setSelectedIds(ids),
            setOnSelectionChange: (callback: (ids: string[]) => void) => modal.setOnSelectionChange(callback),
            open: () => modal.open(),
        });
        controller.render();
        translator.updateUITranslations();

        ChipPickerListener.setup({
            rootEl: pickerRoot,
            scopeKey: "search-brand-picker",
            openBtnEl: pickerRoot,
            chipsEl,
            controller,
        });

        return controller;
    }
}
